<main class="min-h-screen bg-gray-50" role="main">
  <!-- Header with SEO-optimized structure -->
  <header class="bg-blue-400 text-white" role="banner">
    <div class="mx-auto max-w-[1600px] px-4 md:px-8 py-4 md:py-6">
      <div class="max-w-3xl">
        <h1 class="text-xl md:text-2xl lg:text-3xl font-bold mb-2">
          Geschenkgutscheine in {{ displayCity }}
        </h1>
        <p class="text-blue-50 text-sm md:text-base mb-3 md:mb-4 leading-relaxed">
          Du möchtest eine Freude verschenken und dabei deine lokalen Lieblingsgeschäfte
          unterstützen? Entdecke digitale Geschenkgutscheine von Restaurants, Cafés, Boutiquen und
          mehr – direkt von deinen lokalen Lieblingen, sofort per E-Mail!
        </p>
        <div class="flex flex-wrap items-center gap-2">
          <span
            class="inline-flex items-center px-3 py-1.5 bg-blue-300 text-white text-xs font-medium rounded-full"
            [attr.aria-label]="filteredShops.length + ' Geschäfte gefunden'">
            {{ filteredShops.length }} Geschäfte
          </span>
        </div>
      </div>
    </div>
  </header>

  <!-- Main Content -->
  <div class="mx-auto max-w-[1600px] px-4 md:px-8 py-4 md:py-6">
    <!-- Search and Filter Bar -->
    <nav
      class="bg-white shadow-md rounded-lg p-4 md:p-5 mb-5 border border-gray-200"
      aria-label="Stadt- und Kategoriefilter">
      <div class="flex flex-col gap-4">
        <!-- Search -->
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1.5">Stadt wechseln</label>
          <div class="flex gap-2">
            <input
              id="city-search"
              type="text"
              [(ngModel)]="searchCity"
              (keyup.enter)="searchLocation()"
              placeholder="z.B. Berlin, München, Hamburg..."
              class="flex-1 px-3 md:px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-md focus:outline-hidden focus:bg-white focus:ring-2 focus:ring-blue-300 focus:border-transparent text-sm transition-all" />
            <button
              (click)="searchLocation()"
              class="px-4 md:px-6 py-2.5 bg-blue-400 text-white text-sm font-semibold rounded-md hover:bg-blue-500 transition-colors shadow-xs whitespace-nowrap">
              🔍 <span class="hidden sm:inline">Suchen</span>
            </button>
          </div>
        </div>

        <!-- Category Filter - Mobile Optimized -->
        <div class="border-t pt-4 lg:border-t-0 lg:pt-0">
          <label class="block text-xs font-medium text-gray-700 mb-2">Kategorie filtern</label>

          <!-- Mobile: Horizontal Scrollable Categories -->
          <div class="lg:hidden">
            <div
              class="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 snap-x snap-mandatory scrollbar-hide">
              @for (category of categories; track category) {
                <button
                  (click)="filterByCategory(category)"
                  class="px-4 py-2 rounded-full text-sm font-medium transition-all shadow-xs whitespace-nowrap shrink-0 snap-start"
                  [ngClass]="{
                    'bg-blue-500 text-white shadow-md': selectedCategory === category,
                    'bg-gray-100 text-gray-700 border border-gray-300':
                      selectedCategory !== category,
                  }">
                  {{ category === 'all' ? '✨ Alle' : category }}
                </button>
              }
            </div>
          </div>

          <!-- Desktop: Wrapped Categories -->
          <div
            class="hidden lg:flex flex-wrap gap-2 items-center"
            role="group"
            aria-label="Kategorie-Filter">
            @for (category of categories; track category) {
              <button
                (click)="filterByCategory(category)"
                class="px-3 py-1.5 rounded-md text-xs font-medium transition-all shadow-xs"
                [attr.aria-pressed]="selectedCategory === category"
                [attr.aria-label]="
                  category === 'all' ? 'Alle Kategorien anzeigen' : category + ' Geschäfte anzeigen'
                "
                [ngClass]="{
                  'bg-blue-400 text-white hover:bg-blue-500': selectedCategory === category,
                  'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300':
                    selectedCategory !== category,
                }">
                {{ category === 'all' ? 'Alle' : category }}
              </button>
            }
          </div>
        </div>
      </div>
    </nav>

    <!-- Mobile View Tabs (visible only on mobile) -->
    <div class="lg:hidden mb-4">
      <div class="bg-white shadow-xs rounded-lg p-1 flex gap-1">
        <button
          (click)="switchMobileView('map')"
          class="flex-1 py-2.5 px-4 text-sm font-medium rounded-md transition-colors"
          [ngClass]="{
            'bg-blue-400 text-white': mobileView === 'map',
            'text-gray-700 hover:bg-gray-100': mobileView !== 'map',
          }">
          🗺️ Karte
        </button>
        <button
          (click)="switchMobileView('list')"
          class="flex-1 py-2.5 px-4 text-sm font-medium rounded-md transition-colors"
          [ngClass]="{
            'bg-blue-400 text-white': mobileView === 'list',
            'text-gray-700 hover:bg-gray-100': mobileView !== 'list',
          }">
          📋 Liste ({{ filteredShops.length }})
        </button>
      </div>
    </div>

    <!-- Shop List and Map -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Shop List -->
      <aside
        class="lg:col-span-1 bg-white shadow-xs rounded-lg p-3 md:p-4 overflow-y-auto"
        role="complementary"
        [attr.aria-label]="'Liste der Geschäfte in ' + displayCity"
        [ngClass]="{
          'hidden lg:block': mobileView === 'map',
          'max-h-[calc(100vh-280px)] lg:max-h-[700px]': true,
        }">
        <div class="mb-3 md:mb-4 flex items-center justify-between">
          <h2 class="text-sm md:text-base font-semibold text-gray-900">
            {{ (isLoading$ | async) ? 'Lädt...' : filteredShops.length + ' Geschäfte' }}
          </h2>
          <!-- Quick filter indicator on mobile -->
          @if (selectedCategory !== 'all') {
            <span class="lg:hidden text-xs text-blue-600 font-medium">
              {{ selectedCategory }}
            </span>
          }
        </div>

        <!-- Loading State -->
        @if (isLoading$ | async) {
          <div class="flex flex-col items-center justify-center py-12">
            <div class="loading-dots">
              <div class="loading-dots--dot"></div>
              <div class="loading-dots--dot"></div>
              <div class="loading-dots--dot"></div>
            </div>
            <p class="mt-4 text-sm text-gray-500">Geschäfte werden geladen...</p>
          </div>
        }

        <!-- Error State -->
        @if ((loadError$ | async) && !(isLoading$ | async)) {
          <div class="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <p class="text-sm text-red-600">{{ loadError$ | async }}</p>
            <button
              (click)="loadShops()"
              class="mt-3 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors">
              Erneut versuchen
            </button>
          </div>
        }

        <!-- Shop List -->
        @if (!(isLoading$ | async) && !(loadError$ | async)) {
          <ul class="space-y-2.5 list-none" role="list" aria-label="Geschäfte mit Gutscheinen">
            <!-- Selected Shop (shown at top when selected) -->
            @if (selectedShop) {
              <div
                (click)="centerOnShop(selectedShop)"
                class="p-3.5 rounded-lg cursor-pointer transition-all duration-200 relative border-2 border-blue-500 bg-blue-50 shadow-lg">
                <!-- Selected Badge -->
                <div
                  class="absolute top-2 right-2 flex items-center gap-1 bg-blue-600 text-white px-2.5 py-1 rounded-full shadow-xs">
                  <span class="text-[10px] font-bold">✓ AUSGEWÄHLT</span>
                </div>
                <div class="flex gap-2.5 md:gap-3 mb-2">
                  <!-- Logo with fallback -->
                  <div class="shrink-0">
                    @if (getLogoUrl(selectedShop)) {
                      <div class="relative">
                        <img
                          [src]="getLogoUrl(selectedShop)"
                          [alt]="selectedShop.name + ' Logo'"
                          class="w-11 h-11 md:w-12 md:h-12 rounded-full object-cover border-2 border-blue-400 shadow-md"
                          (error)="$any($event.target).style.display = 'none'" />
                      </div>
                    } @else {
                      <div
                        class="w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center text-base md:text-lg font-bold shadow-xs bg-blue-500 text-white">
                        {{ selectedShop.name.charAt(0).toUpperCase() }}
                      </div>
                    }
                  </div>
                  <div class="flex-1 min-w-0">
                    <h3 class="text-sm md:text-sm font-bold text-gray-900 pr-20 md:pr-24 truncate">
                      {{ selectedShop.name }}
                    </h3>
                    <p class="text-xs text-gray-600 mt-0.5 truncate">
                      {{ getCategoryDisplay(selectedShop.category) }}
                    </p>
                  </div>
                </div>
                <p class="text-xs text-gray-600 mb-1 pl-[48px] md:pl-[60px] line-clamp-2">
                  {{ selectedShop.address }}
                </p>
                <!-- Website Link Button -->
                @if (selectedShop.url) {
                  <div class="mb-3 pl-[48px] md:pl-[60px]">
                    <a
                      [href]="addUtmSource(selectedShop.url)"
                      target="_blank"
                      rel="noopener"
                      (click)="$event.stopPropagation()"
                      class="inline-flex items-center gap-1.5 px-2.5 md:px-3 py-1.5 text-xs font-medium text-blue-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-all shadow-xs">
                      <svg
                        class="w-3 md:w-3.5 h-3 md:h-3.5 shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m-9 9a9 9 0 019-9"></path>
                      </svg>
                      <span class="truncate max-w-[100px] md:max-w-[120px]">{{
                        extractDomain(selectedShop.url)
                      }}</span>
                    </a>
                  </div>
                }
                <!-- Voucher Button -->
                @if (selectedShop.voucherUrl) {
                  <div class="mt-3">
                    <a
                      [href]="addUtmSource(selectedShop.voucherUrl)"
                      target="_blank"
                      rel="noopener"
                      (click)="$event.stopPropagation()"
                      class="block w-full px-3 py-2.5 text-xs md:text-sm font-semibold rounded-md transition-all bg-blue-500 text-white hover:bg-blue-600 shadow-md text-center active:scale-95">
                      🎁 Gutschein kaufen
                    </a>
                  </div>
                }
              </div>
            }
            <!-- Divider when selected shop is shown -->
            @if (selectedShop) {
              <div class="border-t border-gray-200 pt-2">
                <p class="text-xs text-gray-500 text-center mb-2">Alle Geschäfte</p>
              </div>
            }
            <!-- Regular Shop List -->
            @for (shop of filteredShops; track shop) {
              <li
                (click)="centerOnShop(shop)"
                (mouseenter)="onShopHover(shop)"
                (mouseleave)="onShopHoverEnd(shop)"
                role="listitem"
                [attr.aria-label]="shop.name + ' - ' + getCategoryDisplay(shop.category)"
                class="p-3.5 rounded-lg cursor-pointer transition-all duration-200 relative border"
                [ngClass]="{
                  'bg-white border-blue-300 shadow-xs':
                    shop.isSchenkwerkPartner &&
                    selectedShop?.id !== shop.id &&
                    hoveredShop?.id !== shop.id,
                  'bg-blue-50 border-blue-400 shadow-md':
                    shop.isSchenkwerkPartner &&
                    (selectedShop?.id === shop.id || hoveredShop?.id === shop.id),
                  'bg-white border-gray-200':
                    !shop.isSchenkwerkPartner &&
                    selectedShop?.id !== shop.id &&
                    hoveredShop?.id !== shop.id,
                  'bg-gray-50 border-gray-300 shadow-md':
                    !shop.isSchenkwerkPartner &&
                    (selectedShop?.id === shop.id || hoveredShop?.id === shop.id),
                  'opacity-50':
                    !shop.isSchenkwerkPartner &&
                    hoveredShop?.id !== shop.id &&
                    selectedShop?.id !== shop.id,
                  'scale-[1.02]': hoveredShop?.id === shop.id,
                  'z-10': hoveredShop?.id === shop.id,
                }">
                <!-- Partner Badge -->
                @if (shop.isSchenkwerkPartner) {
                  <div
                    class="absolute top-2 right-2 flex items-center gap-1 bg-blue-500 text-white px-2.5 py-1 rounded-full shadow-xs">
                    <span class="text-[10px] font-bold">⭐ PARTNER</span>
                  </div>
                }
                <div class="flex gap-2.5 md:gap-3 mb-2">
                  <!-- Logo with fallback -->
                  <div class="shrink-0">
                    @if (getLogoUrl(shop)) {
                      <div class="relative">
                        <img
                          [src]="getLogoUrl(shop)"
                          [alt]="shop.name + ' Logo'"
                          class="w-11 h-11 md:w-12 md:h-12 rounded-full object-cover border-2 shadow-md"
                          [ngClass]="{
                            'border-blue-300': shop.isSchenkwerkPartner,
                            'border-gray-300': !shop.isSchenkwerkPartner,
                          }"
                          (error)="$any($event.target).style.display = 'none'" />
                      </div>
                    } @else {
                      <div
                        class="w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center text-base md:text-lg font-bold shadow-xs"
                        [ngClass]="{
                          'bg-blue-500 text-white': shop.isSchenkwerkPartner,
                          'bg-gray-200 text-gray-600': !shop.isSchenkwerkPartner,
                        }">
                        {{ shop.name.charAt(0).toUpperCase() }}
                      </div>
                    }
                  </div>
                  <div class="flex-1 min-w-0">
                    <h3 class="text-sm md:text-sm font-bold text-gray-900 pr-14 md:pr-16 truncate">
                      {{ shop.name }}
                    </h3>
                    <p class="text-xs text-gray-600 mt-0.5 truncate">
                      {{ getCategoryDisplay(shop.category) }}
                    </p>
                  </div>
                </div>
                <p class="text-xs text-gray-600 mb-1 pl-[48px] md:pl-[60px] line-clamp-2">
                  {{ shop.address }}
                </p>
                <!-- Website Link Button -->
                @if (shop.url) {
                  <div class="mb-3 pl-[48px] md:pl-[60px]">
                    <a
                      [href]="addUtmSource(shop.url)"
                      target="_blank"
                      rel="noopener"
                      (click)="$event.stopPropagation()"
                      class="inline-flex items-center gap-1.5 px-2.5 md:px-3 py-1.5 text-xs font-medium text-blue-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-all shadow-xs">
                      <svg
                        class="w-3 md:w-3.5 h-3 md:h-3.5 shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"></path>
                      </svg>
                      <span class="truncate max-w-[100px] md:max-w-[120px]">{{
                        extractDomain(shop.url)
                      }}</span>
                    </a>
                  </div>
                }
                <!-- Voucher Button (Schenkwerk Partners with storefront enabled) -->
                @if (shop.voucherUrl) {
                  <div class="mt-3">
                    <a
                      [href]="addUtmSource(shop.voucherUrl)"
                      target="_blank"
                      rel="noopener"
                      (click)="$event.stopPropagation()"
                      class="block w-full px-3 py-2.5 text-xs md:text-sm font-semibold rounded-md transition-all bg-blue-500 text-white hover:bg-blue-600 shadow-md text-center active:scale-95">
                      🎁 Gutschein kaufen
                    </a>
                  </div>
                }
                <!-- Cross-Selling for Non-Partners -->
                @if (!shop.isSchenkwerkPartner) {
                  <div class="mt-2.5 pt-2.5 border-t border-gray-200">
                    <p class="text-[10px] md:text-xs text-gray-500 text-center leading-relaxed">
                      Geschäftsinhaber?
                      <a
                        href="https://schenkwerk.com"
                        target="_blank"
                        rel="noopener"
                        class="text-blue-500 font-semibold underline hover:no-underline">
                        Partner werden
                      </a>
                    </p>
                  </div>
                }
              </li>
            }
          </ul>
        }
      </aside>

      <!-- Map -->
      <section
        class="lg:col-span-2 bg-white shadow-xs rounded-lg overflow-hidden"
        [attr.aria-label]="'Karte mit Geschäften in ' + displayCity"
        [ngClass]="{
          'hidden lg:block': mobileView === 'list',
        }">
        <div
          #mapContainer
          class="w-full h-[calc(100vh-280px)] lg:h-[700px]"
          style="min-height: 400px"
          role="application"
          aria-label="Interaktive Karte mit Geschäften"></div>
      </section>
    </div>

    <!-- Info Boxes -->
    <footer class="mt-6 space-y-4" role="contentinfo">
      <!-- Call to Action for Business Owners -->
      <div class="bg-blue-400 rounded-lg p-4 md:p-6 text-white shadow-lg">
        <div
          class="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 md:gap-4">
          <div class="flex-1">
            <h3 class="text-base md:text-lg font-semibold mb-1 md:mb-2">
              Für Geschäftsinhaber in {{ displayCity }}
            </h3>
            <p class="text-blue-100 text-xs md:text-sm">
              Werde Schenkwerk-Partner und erreiche neue Kunden mit digitalen Geschenkgutscheinen.
            </p>
          </div>
          <a
            href="https://schenkwerk.com"
            target="_blank"
            class="w-full md:w-auto px-5 py-2.5 bg-white text-blue-500 font-medium text-sm rounded-md hover:bg-blue-50 transition-colors whitespace-nowrap text-center">
            Partner werden →
          </a>
        </div>
      </div>

      <!-- Schenkwerk Partner Info -->
      <div class="bg-gray-50 rounded-lg p-3">
        <p class="text-xs text-gray-500 leading-relaxed">
          <span class="font-semibold text-gray-600">Schenkwerk Partner-Geschäfte:</span>
          Geschäfte mit dem PARTNER-Badge sind offizielle Schenkwerk-Partner und werden
          hervorgehoben. Diese Geschäfte bieten exklusive Gutschein-Angebote über
          <a
            href="https://schenkwerk.com"
            target="_blank"
            class="text-blue-500 underline hover:text-blue-600"
            >schenkwerk.com</a
          >.
          <span class="font-semibold text-gray-600 block mt-2">Vorteil für Ladenbetreiber:</span>
          Mit Schenkwerk ist der Gutschein direkt an dein Geschäft gebunden – deine Kunden kommen
          garantiert zu dir und nicht zur Konkurrenz!
        </p>
      </div>

      <!-- General Info -->
      <div class="bg-gray-50 rounded-lg p-3">
        <p class="text-xs text-gray-500 leading-relaxed">
          <span class="font-semibold text-gray-600">Über diese Karte:</span>
          Diese Karte zeigt Geschäfte in {{ displayCity }}, die Gutscheine anbieten. Klicke auf
          einen Shop-Marker, um weitere Details anzuzeigen oder die Website zu besuchen. Nutze die
          Kategoriefilter, um bestimmte Geschäftstypen zu finden. Die Korrektheit der Angaben ist
          ohne Gewähr.
        </p>
      </div>
    </footer>

    <!-- SEO Content Section -->
    <article
      class="mt-8 bg-white shadow-xs rounded-lg p-4 md:p-6 space-y-4 md:space-y-6"
      itemscope
      itemtype="https://schema.org/Article">
      <div>
        <h2 class="text-2xl font-bold text-gray-900 mb-3" itemprop="headline">
          Geschenkgutscheine in {{ displayCity }} – Das perfekte Geschenk für jeden Anlass
        </h2>
        <p class="text-gray-700 leading-relaxed">
          Ob zum Geburtstag, zu Weihnachten oder einfach als spontane Aufmerksamkeit –
          Geschenkgutscheine sind die ideale Lösung, wenn du jemandem eine Freude machen möchtest.
          In {{ displayCity }} findest du eine vielfältige Auswahl an lokalen Geschäften,
          Restaurants, Cafés und Wellness-Einrichtungen, die Gutscheine anbieten. Mit einem
          Geschenkgutschein schenkst du nicht nur Flexibilität, sondern unterstützt auch die lokale
          Wirtschaft in {{ displayCity }}.
        </p>
      </div>

      <div>
        <h3 class="text-xl font-semibold text-gray-900 mb-3">
          Warum Geschenkgutscheine aus {{ displayCity }}?
        </h3>
        <div class="grid md:grid-cols-2 gap-4">
          <div class="space-y-2">
            <div class="flex items-start gap-3">
              <span class="text-blue-500 font-bold">✓</span>
              <p class="text-gray-700">
                <strong>Lokale Unterstützung:</strong> Stärke die Wirtschaft in {{ displayCity }}
              </p>
            </div>
            <div class="flex items-start gap-3">
              <span class="text-blue-500 font-bold">✓</span>
              <p class="text-gray-700">
                <strong>Große Auswahl:</strong> Von Cafés über Restaurants bis zu Wellness-Angeboten
              </p>
            </div>
            <div class="flex items-start gap-3">
              <span class="text-blue-500 font-bold">✓</span>
              <p class="text-gray-700">
                <strong>Gezielt:</strong> Schenke einen Gutschein für das Lieblingsgeschäft
              </p>
            </div>
          </div>
          <div class="space-y-2">
            <div class="flex items-start gap-3">
              <span class="text-blue-500 font-bold">✓</span>
              <p class="text-gray-700">
                <strong>Einfach online:</strong> Gutscheine bequem von zu Hause kaufen
              </p>
            </div>
            <div class="flex items-start gap-3">
              <span class="text-blue-500 font-bold">✓</span>
              <p class="text-gray-700">
                <strong>Persönlich:</strong> Unterstütze lokale Lieblingsläden
              </p>
            </div>
            <div class="flex items-start gap-3">
              <span class="text-blue-500 font-bold">✓</span>
              <p class="text-gray-700">
                <strong>Nachhaltig:</strong> Kein Versand, keine Verpackung nötig
              </p>
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 class="text-xl font-semibold text-gray-900 mb-3">
          Beliebte Kategorien für Geschenkgutscheine in {{ displayCity }}
        </h3>
        <p class="text-gray-700 leading-relaxed mb-3">
          In {{ displayCity }} findest du Geschenkgutscheine für die unterschiedlichsten
          Gelegenheiten:
        </p>
        <div class="grid md:grid-cols-3 gap-4">
          <div class="p-4 bg-gray-50 rounded-lg">
            <h4 class="font-semibold text-gray-900 mb-2">🍽️ Restaurants & Cafés</h4>
            <p class="text-sm text-gray-600">
              Kulinarische Erlebnisse für Feinschmecker und Genießer
            </p>
          </div>
          <div class="p-4 bg-gray-50 rounded-lg">
            <h4 class="font-semibold text-gray-900 mb-2">💆 Wellness & Massage</h4>
            <p class="text-sm text-gray-600">Entspannung und Erholung für Körper und Geist</p>
          </div>
          <div class="p-4 bg-gray-50 rounded-lg">
            <h4 class="font-semibold text-gray-900 mb-2">🌸 Blumenläden</h4>
            <p class="text-sm text-gray-600">Frische Blumen für besondere Momente</p>
          </div>
        </div>
      </div>

      <div class="border-t pt-6">
        <h3 class="text-xl font-semibold text-gray-900 mb-3">
          Über Schenkwerk – Deine Plattform für digitale Geschenkgutscheine
        </h3>
        <p class="text-gray-700 leading-relaxed mb-3">
          <a
            href="https://schenkwerk.com"
            target="_blank"
            class="text-blue-500 font-semibold hover:underline"
            >Schenkwerk.com</a
          >
          ist die moderne Plattform für digitale Geschenkgutscheine in Deutschland. Wir verbinden
          lokale Geschäfte mit Kunden, die auf der Suche nach dem perfekten Geschenk sind. Unsere
          Partner-Geschäfte profitieren von erhöhter Sichtbarkeit, neuen Kunden und einem einfachen
          digitalen Gutschein-System.
        </p>
        <p class="text-gray-700 leading-relaxed" itemprop="articleBody">
          Du bist Geschäftsinhaber in {{ displayCity }} und möchtest auch Geschenkgutscheine
          anbieten?
          <a
            href="https://schenkwerk.com"
            target="_blank"
            rel="noopener"
            class="text-blue-500 font-semibold hover:underline">
            Werde jetzt Schenkwerk-Partner
          </a>
          und erschließe neue Umsatzquellen mit digitalen Gutscheinen. Keine Setup-Gebühren, keine
          versteckten Kosten – starte noch heute!
        </p>
      </div>
      <!-- Hidden SEO metadata -->
      <meta itemprop="author" content="Schenkwerk" />
      <meta itemprop="datePublished" content="2025-01-01" />
      <meta itemprop="publisher" content="Schenkwerk GmbH" />
    </article>
  </div>
</main>
